import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  MobileStepper,
} from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

type SliderImage = {
  src: string;
  alt: string;
};

type ImageSliderProps = {
  images: SliderImage[];
  autoPlayInterval?: number;
  height?: number | string;
};

function ImageSlider({
  images,
  autoPlayInterval = 4000,
  height = 420,
}: ImageSliderProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const maxSteps = images.length;

  const hasImages = useMemo(() => maxSteps > 0, [maxSteps]);

  const handleNext = () => {
    setActiveStep((prevStep) => (prevStep + 1) % maxSteps);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => (
      prevStep === 0 ? maxSteps - 1 : prevStep - 1
    ));
  };

  useEffect(() => {
    if (!hasImages || isPaused || maxSteps <= 1) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setActiveStep((prevStep) => (prevStep + 1) % maxSteps);
    }, autoPlayInterval);

    return () => {
      window.clearInterval(timer);
    };
  }, [autoPlayInterval, hasImages, isPaused, maxSteps]);

  if (!hasImages) {
    return null;
  }

  return (
    <Box
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      sx={{
        position: 'relative',
        width: '100%',
        height,
        overflow: 'hidden',
        borderRadius: 2,
      }}
    >
      {images.map((image, index) => (
        <Box
          key={image.src}
          component="img"
          src={image.src}
          alt={image.alt}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            opacity: index === activeStep ? 1 : 0,
            transition: 'opacity 600ms ease-in-out',
          }}
        />
      ))}

      {maxSteps > 1 && (
        <>
          <IconButton
            aria-label="Previous image"
            onClick={handleBack}
            sx={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.35)',
              color: '#fff',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.55)',
              },
            }}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>

          <IconButton
            aria-label="Next image"
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.35)',
              color: '#fff',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.55)',
              },
            }}
          >
            <KeyboardArrowRightIcon />
          </IconButton>

          <MobileStepper
            variant="dots"
            steps={maxSteps}
            position="static"
            activeStep={activeStep}
            nextButton={null}
            backButton={null}
            sx={{
              position: 'absolute',
              bottom: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.25)',
              borderRadius: 10,
              px: 2,
              py: 0.5,
              '& .MuiMobileStepper-dot': {
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
              },
              '& .MuiMobileStepper-dotActive': {
                backgroundColor: '#fff',
              },
            }}
          />
        </>
      )}
    </Box>
  );
}

export default ImageSlider;
